import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  showBookingErrorToast,
  showBookingSuccessToast,
} from "../components/bookingToast";

import { CELL_SIZE } from "./constants";

import {
  ADMIN_MAX_DATE_RANGE_DAYS,
  getAdminMaxRangeEndDate,
  getAdminMaxSelectableDate,
  clamp,
  findOpenPosition,
  getSectionShape,
  generateDateRange,
  generateRecurringDates,
  getLocalISODate,
} from "./utils";

import BookingHeader from "./components/BookingHeader";
import GlobalBookingsList from "./components/GlobalBookingsList";
import AddSectionSidebar from "./components/AddSectionSidebar";
import OfficeGrid from "./components/OfficeGrid";
import SectionSettingsSidebar from "./components/SectionSettingsSidebar";
import SectionManagementModal from "./components/SectionManagementModal";
import { ConfirmDialog, PromptDialog } from "../components/Dialogs";

const createSectionForm = () => ({
  name: "",
  type: "rectangle",
});

const getValidDefaultDate = () => {
  const d = new Date();
  if (d.getDay() === 6) d.setDate(d.getDate() + 2); // Sat -> Mon
  if (d.getDay() === 0) d.setDate(d.getDate() + 1); // Sun -> Mon
  return getLocalISODate(d);
};

const createBookingForm = (seatId = "") => {
  const today = getValidDefaultDate();
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  return {
    isBlockedSeat: false,
    bookedFor: "",
    bookingMode: "specific", // "specific" | "range" | "recurring" | "permanent"
    dates: [today],
    startDate: today,
    endDate: getLocalISODate(nextWeek),
    weekdays: [1, 2, 3, 4, 5], // Mon–Fri default
    seatId,
    requirement: "laptop",
    note: "",
    startTime: "09:00",
    endTime: "18:00",
    ignoreSeatTypeRule: true, // admin always overrides seat-type rule
  };
};

const isDateWithinAdminWindow = (dateValue) => {
  if (!dateValue) return false;
  const date = new Date(`${dateValue}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = getAdminMaxSelectableDate(today);
  return date >= today && date <= maxDate;
};

const BOOKING_TYPE_QUERY_MAP = {
  permanent: "Permanent",
  recurring: "Recurring",
  "one-time": "One-time",
  "date-range": "Date-Range",
};

const SEAT_CAPABILITY_QUERY_MAP = {
  all: undefined,
  monitor: "Monitor",
  laptop: "Laptop",
  desktop: "Desktop",
};


const Booking = () => {
  const dispatch = useDispatch();
  const workspace = useSelector((state) => state.adminBookings.workspace);
  const sectionBookings = useSelector((state) => state.adminBookings.sectionBookings);
  const allAdminBookings = useSelector((state) => state.adminBookings.allAdminBookings);
  const allAdminBookingsPagination = useSelector(
    (state) => state.adminBookings.allAdminBookingsPagination
  );
  const users = useSelector((state) => state.adminBookings.users);
  const loading = useSelector((state) => state.adminBookings.loading);
  const selectedSeatDetails = useSelector((state) => state.adminBookings.selectedSeatDetails);

  const [sectionForm, setSectionForm] = useState(createSectionForm());
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [sectionSize, setSectionSize] = useState({ width: "", height: "" });
  const [seatDimensions, setSeatDimensions] = useState({ rows: "", columns: "" });
  const [dragState, setDragState] = useState(null);
  const [previewPosition, setPreviewPosition] = useState(null);
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [ignoreNextClick, setIgnoreNextClick] = useState(false);
  const [bookingForm, setBookingForm] = useState(createBookingForm());

  const [filterDate, setFilterDate] = useState(getValidDefaultDate());
  const [filterType, setFilterType] = useState("all");
  const [filterSeatCapability, setFilterSeatCapability] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [bookingsPage, setBookingsPage] = useState(1);

  const [activeSeatSlot, setActiveSeatSlot] = useState(null);
  const [newSeatLabel, setNewSeatLabel] = useState("");
  const [newSeatType, setNewSeatType] = useState("laptop");
  const seatLabelInputRef = useRef(null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [promptDialog, setPromptDialog] = useState({ isOpen: false });

  useEffect(() => {
    const fetchWorkspace = () => {
      dispatch.adminBookings.loadWorkspace().catch((error) => {
        toast.error(error.message || "Unable to load booking layout.");
      });
    };

    fetchWorkspace();
    dispatch.adminBookings.loadUsers();

    const handleStorageChange = (e) => {
      if (e.key === "tblr-admin-seat-booking-workspace-v2") {
        fetchWorkspace();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch]);

  const fetchGlobalBookings = React.useCallback(() => {
    const typeParam = filterType === "all" ? undefined : BOOKING_TYPE_QUERY_MAP[filterType];
    const seatCapabilityParam = SEAT_CAPABILITY_QUERY_MAP[filterSeatCapability];
    const searchParam = searchTerm.trim() || undefined;

    dispatch.adminBookings
      .loadAllAdminBookings({
        date: filterDate,
        type: typeParam,
        seat_capability: seatCapabilityParam,
        search: searchParam,
        page: bookingsPage,
        limit: 10,
      })
      .catch((error) => {
        toast.error(error.message || "Unable to load bookings for that date.");
      });
  }, [dispatch, filterDate, filterType, filterSeatCapability, bookingsPage, searchTerm]);

  useEffect(() => {
    fetchGlobalBookings();
  }, [fetchGlobalBookings]);

  // If we are on a page that no longer exists (e.g. after deletion), go back
  const totalPages = allAdminBookingsPagination?.totalPages || 0;
  useEffect(() => {
    if (totalPages === 0 && bookingsPage !== 1) {
      setBookingsPage(1);
    } else if (bookingsPage > totalPages && totalPages > 0) {
      setBookingsPage(totalPages);
    }
  }, [bookingsPage, totalPages]);

  const office = workspace.office || {
    name: "Office Layout",
    gridColumns: 12,
    gridRows: 10,
  };

  const handleFilterDateChange = (date) => {
    setBookingsPage(1);
    setFilterDate(date);
  };

  const sections = useMemo(() => workspace.sections || [], [workspace.sections]);
  const bookings = useMemo(() => workspace.bookings || [], [workspace.bookings]);

  // Handle auto-focusing a section/seat on mount if sectionId/seatId is provided in query params
  useEffect(() => {
    if (sections && sections.length > 0) {
      const params = new URLSearchParams(window.location.search);
      const paramSectionId = params.get("sectionId");
      const paramSeatId = params.get("seatId");
      
      if (paramSectionId) {
        const sectionExists = sections.some((s) => s.id === paramSectionId);
        if (sectionExists) {
          setSelectedSectionId(paramSectionId);
          setIsSectionModalOpen("bookings");
          
          if (paramSeatId) {
            setBookingForm((prev) => ({ ...prev, seatId: paramSeatId }));
          }
        }
      }
    }
  }, [sections]);

  // Fetch section-specific bookings when a section is opened.
  // Fetch section-specific bookings when a section is opened
  useEffect(() => {
    if (isSectionModalOpen && selectedSectionId) {
      dispatch.adminBookings.loadSectionBookings({ 
        sectionId: selectedSectionId, 
        date: filterDate 
      });
    }
  }, [isSectionModalOpen, selectedSectionId, filterDate, dispatch]);

  const selectedSection = useMemo(
    () => sections.find((section) => section.id === selectedSectionId) || null,
    [sections, selectedSectionId]
  );

  // Bookings for the selected date + any permanent bookings (shown on every date)
  const allBookingsByDate = useMemo(() => {
    return allAdminBookings
      .filter((b) => b.status === "active")
      .map((booking) => {
        const section = sections.find((s) => s.id === booking.sectionId);
        const seat = section?.seats.find((s) => s.id === booking.seatId);
        const user = users.find((u) => u.id === booking.bookedFor);
        return {
          ...booking,
          bookedFor: booking.userName || (user ? user.fullName : booking.bookedFor),
          sectionName: booking.sectionName || section?.name || "Unknown section",
          seatLabel: booking.seatLabel || seat?.label || "—",
        };
      });
  }, [allAdminBookings, sections, users]);

  // Preview count of dates that will be booked given current form state
  const previewDateCount = useMemo(() => {
    const { bookingMode, dates, startDate, endDate, weekdays } = bookingForm;
    if (bookingMode === "permanent") return null;
    if (bookingMode === "specific") return dates.filter(Boolean).length;
    if (!startDate || !endDate || new Date(startDate) > new Date(endDate)) return 0;
    if (bookingMode === "range") return generateDateRange(startDate, endDate).length;
    if (bookingMode === "recurring") {
      return weekdays.length === 0
        ? 0
        : generateRecurringDates(startDate, endDate, weekdays).length;
    }
    return 0;
  }, [bookingForm]);

  useEffect(() => {
    if (selectedSection) {
      setSectionSize({
        width: String(selectedSection.displayWidth || selectedSection.width),
        height: String(selectedSection.displayHeight || selectedSection.height),
      });
      setSeatDimensions({
        rows: String(selectedSection.seatRows || 2),
        columns: String(selectedSection.seatColumns || 6),
      });
    } else {
      setSectionSize({ width: "", height: "" });
      setSeatDimensions({ rows: "", columns: "" });
    }
  }, [selectedSection]);

  useEffect(() => {
    if (selectedSectionId) {
      setBookingForm((current) => ({
        ...createBookingForm(""),
        bookedFor: current.bookedFor,
        bookingMode: current.bookingMode,
      }));
    } else {
      setBookingForm(createBookingForm());
    }
    setActiveSeatSlot(null);
    setNewSeatLabel("");
  }, [selectedSectionId]);

  useEffect(() => {
    if (
      selectedSectionId &&
      !sections.some((section) => section.id === selectedSectionId)
    ) {
      setSelectedSectionId("");
    }
  }, [sections, selectedSectionId]);

  useEffect(() => {
    if (!dragState) return undefined;

    const handleMouseMove = (event) => {
      const nextX = clamp(
        dragState.originX +
          Math.round((event.clientX - dragState.startClientX) / CELL_SIZE),
        1,
        office.gridColumns - dragState.width + 1
      );
      const nextY = clamp(
        dragState.originY +
          Math.round((event.clientY - dragState.startClientY) / CELL_SIZE),
        1,
        office.gridRows - dragState.height + 1
      );
      setPreviewPosition({ sectionId: dragState.sectionId, x: nextX, y: nextY });
    };

    const handleMouseUp = async () => {
      const currentSection = sections.find(
        (section) => section.id === dragState.sectionId
      );
      const finalPosition = previewPosition || {
        sectionId: dragState.sectionId,
        x: dragState.originX,
        y: dragState.originY,
      };

      setDragState(null);

      if (!currentSection) {
        setPreviewPosition(null);
        return;
      }

      if (
        finalPosition.x === dragState.originX &&
        finalPosition.y === dragState.originY
      ) {
        setPreviewPosition(null);
        return;
      }

      setIgnoreNextClick(true);

      try {
        await dispatch.adminBookings.updateSection({
          ...currentSection,
          x: finalPosition.x,
          y: finalPosition.y,
        });
        toast.success("Section moved.");
      } catch (error) {
        toast.error(error.message || "Unable to move section there.");
      } finally {
        setPreviewPosition(null);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dispatch, dragState, office.gridColumns, office.gridRows, previewPosition, sections]);

  const selectedSectionSeatLimit = selectedSection
    ? (selectedSection.seatRows || 2) * (selectedSection.seatColumns || 6)
    : 1;

  // Section form
  const handleSectionFormChange = ({ target }) => {
    setSectionForm((current) => ({ ...current, [target.name]: target.value }));
  };

  const handleCreateSection = async (event) => {
    event.preventDefault();
    const shape = getSectionShape(sectionForm.type);
    const position = findOpenPosition(sections, office, shape);
    if (!position) {
      toast.error("No free space is available for this section type.");
      return;
    }
    try {
      const result = await dispatch.adminBookings.createSection({
        name: sectionForm.name,
        type: sectionForm.type,
        x: position.x,
        y: position.y,
        width: shape.width,
        height: shape.height,
        notes: "",
      });
      setSelectedSectionId(result.section.id);
      setSectionForm(createSectionForm());
      toast.success("Section added.");
    } catch (error) {
      toast.error(error.message || "Unable to add section.");
    }
  };

  const handleSectionMouseDown = (event, section) => {
    event.preventDefault();
    setSelectedSectionId(section.id);
    setPreviewPosition({ sectionId: section.id, x: section.x, y: section.y });
    setDragState({
      sectionId: section.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: section.x,
      originY: section.y,
      width: section.displayWidth || section.width,
      height: section.displayHeight || section.height,
    });
  };

  const handleSectionClick = (section) => {
    setSelectedSectionId(section.id);
    if (ignoreNextClick) {
      setIgnoreNextClick(false);
      return;
    }
    // Selection details are now shown in the right panel.
    // Modal is opened via the "Seats" button in the sidebar.
  };

  const handleUpdateSection = async (sectionData) => {
    if (!selectedSection) return;

    const oldRows = selectedSection.seatRows || 2;
    const oldCols = selectedSection.seatColumns || 6;
    const newRows = Number(sectionData.seatRows) || 2;
    const newCols = Number(sectionData.seatColumns) || 6;

    // Identify seats that fall outside the new grid dimensions
    const orphanedSeats = selectedSection.seats.filter(
      (seat) => seat.row > newRows || seat.column > newCols
    );

    // If no seats are orphaned, just update normally
    if (orphanedSeats.length === 0) {
      try {
        await dispatch.adminBookings.updateSection(sectionData);
        toast.success("Section updated.");
      } catch (error) {
        toast.error(error.message || "Unable to update section.");
      }
      return;
    }

    // Check orphaned seats for active bookings or permanent assignments
    const activeBookings = workspace.bookings.filter(
      (b) =>
        b.status === "active" &&
        orphanedSeats.some((seat) => seat.id === b.seatId)
    );

    const seatsWithPermanentAssignment = orphanedSeats.filter(
      (seat) => seat.permanentUserId
    );

    // Block if any orphaned seat has active bookings or permanent assignments
    if (activeBookings.length > 0 || seatsWithPermanentAssignment.length > 0) {
      const blockedSeatLabels = [
        ...new Set([
          ...activeBookings.map((b) => {
            const seat = orphanedSeats.find((s) => s.id === b.seatId);
            return seat?.label || b.seatId;
          }),
          ...seatsWithPermanentAssignment.map((s) => s.label || s.id),
        ]),
      ];

      const reasons = [];
      if (activeBookings.length > 0) {
        reasons.push(`${activeBookings.length} active booking(s)`);
      }
      if (seatsWithPermanentAssignment.length > 0) {
        reasons.push(
          `${seatsWithPermanentAssignment.length} permanent assignment(s)`
        );
      }

      toast.error(
        `Cannot reduce dimensions. Seat(s) ${blockedSeatLabels.join(
          ", "
        )} would be removed but have ${reasons.join(
          " and "
        )}. Please clear those first.`,
        { autoClose: 8000 }
      );
      return;
    }

    // Confirm with admin before removing orphaned seats
    setConfirmDialog({
      isOpen: true,
      title: "Remove Out-of-Bounds Seats",
      message: `Changing layout from ${oldRows}×${oldCols} to ${newRows}×${newCols} will remove ${
        orphanedSeats.length
      } seat(s) (${orphanedSeats
        .map((s) => s.label)
        .join(", ")}) that fall outside the new grid. Continue?`,
      isDestructive: true,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false });
        try {
          // First update the section dimensions
          await dispatch.adminBookings.updateSection(sectionData);

          // Then remove orphaned seats
          for (const seat of orphanedSeats) {
            await dispatch.adminBookings.deleteSeat({ seatId: seat.id });
          }

          // Clean up bookingForm if the selected seat was orphaned
          if (orphanedSeats.some((s) => s.id === bookingForm.seatId)) {
            setBookingForm((prev) => ({ ...prev, seatId: "" }));
          }

          toast.success(
            `Section updated. ${orphanedSeats.length} out-of-bounds seat(s) removed.`
          );
          fetchGlobalBookings();
        } catch (error) {
          toast.error(error.message || "Unable to update section.");
        }
      },
      onCancel: () => setConfirmDialog({ isOpen: false }),
    });
  };

  const handleDeleteSection = () => {
    if (!selectedSection) return;
    setConfirmDialog({
      isOpen: true,
      title: "Delete Section",
      message: `Are you sure you want to delete ${selectedSection.name}?`,
      isDestructive: true,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false });
        try {
          await dispatch.adminBookings.deleteSection({ sectionId: selectedSection.id });
          setSelectedSectionId("");
          toast.success("Section deleted.");
          fetchGlobalBookings();
        } catch (error) {
          toast.error(error.message || "Unable to delete section.");
        }
      },
      onCancel: () => setConfirmDialog({ isOpen: false }),
    });
  };

  // Seat naming
  const handleEmptySlotClick = (row, column) => {
    setActiveSeatSlot({ row, column });
    setNewSeatLabel("");
    setTimeout(() => seatLabelInputRef.current?.focus(), 50);
  };

  const handleSeatNameSubmit = async (event) => {
    event.preventDefault();
    if (!selectedSection || !activeSeatSlot) return;
    const label = newSeatLabel.trim();
    if (!label) {
      toast.error("Enter a seat number or name.");
      return;
    }
    try {
      await dispatch.adminBookings.createSeat({
        sectionId: selectedSection.id,
        seatData: {
          label,
          row: activeSeatSlot.row,
          column: activeSeatSlot.column,
          type: newSeatType,
          status: "available",
        },
      });
      setActiveSeatSlot(null);
      setNewSeatLabel("");
      toast.success(`Seat "${label}" added.`);
    } catch (error) {
      toast.error(error.message || "Unable to add seat.");
    }
  };

  const handleUpdateSeat = async (seatId, seatData) => {
    try {
      await dispatch.adminBookings.updateSeat({ id: seatId, seatData });
      
      // If permanent assignment is provided, update it separately
      if (seatData.permanentUserId !== undefined) {
        await dispatch.adminBookings.assignPermanentSeat({
          seatId,
          userId: seatData.permanentUserId,
        });
      }
      toast.success("Seat updated.");
    } catch (error) {
      toast.error(error.message || "Unable to update seat.");
    }
  };

  const handleDeleteSeat = (seatId) => {
    if (!selectedSection) return;
    setConfirmDialog({
      isOpen: true,
      title: "Remove Seat",
      message: "Are you sure you want to remove this seat?",
      isDestructive: true,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false });
        try {
          await dispatch.adminBookings.deleteSeat({ sectionId: selectedSection.id, seatId });
          if (bookingForm.seatId === seatId) {
            setBookingForm((prev) => ({
              ...prev,
              seatId: selectedSection.seats.find((s) => s.id !== seatId)?.id || "",
            }));
          }
          toast.success("Seat removed.");
          fetchGlobalBookings();
        } catch (error) {
          toast.error(error.message || "Unable to remove seat.");
        }
      },
      onCancel: () => setConfirmDialog({ isOpen: false }),
    });
  };

 


  const handleRemoveDate = (index) => {
    setBookingForm((prev) => ({
      ...prev,
      dates: prev.dates.filter((_, i) => i !== index),
    }));
  };

  const handleBookingDateChange = (event, index) => {
    const value = event.target.value;
    if (value) {
      const d = new Date(value);
      if (d.getDay() === 0 || d.getDay() === 6) {
        toast.error("Weekend bookings are not allowed.");
        return;
      }
    }
    setBookingForm((prev) => ({
      ...prev,
      dates: prev.dates.map((d, i) => (i === index ? value : d)),
    }));
  };

  const handleToggleWeekday = (day) => {
    setBookingForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter((d) => d !== day)
        : [...prev.weekdays, day].sort(),
    }));
  };

  const handleCreateBooking = async (event) => {
    event.preventDefault();
    if (!selectedSection) return;

    if (!bookingForm.isBlockedSeat && !bookingForm.bookedFor) {
      toast.error("Please select a user to book the seat for.");
      return;
    }

    const { bookingMode, dates, startDate, endDate, weekdays } = bookingForm;

    let datesToBook = [];
    let bookingType = "one-time";

    if (bookingMode === "permanent") {
      datesToBook = ["permanent"];
      bookingType = "permanent";
    } else if (bookingMode === "specific") {
      datesToBook = dates.filter(Boolean);
      if (datesToBook.some((date) => !isDateWithinAdminWindow(date))) {
        toast.error(`Admin bookings are limited to the next ${ADMIN_MAX_DATE_RANGE_DAYS} days.`);
        return;
      }
    } else if (bookingMode === "range") {
      if (!startDate || !endDate) {
        toast.error("Set both a start and end date.");
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        toast.error("Start date must be before end date.");
        return;
      }
      if (!isDateWithinAdminWindow(startDate)) {
        toast.error(`Admin bookings are limited to the next ${ADMIN_MAX_DATE_RANGE_DAYS} days.`);
        return;
      }
      datesToBook = [startDate];
      bookingType = "date-range";
    } else if (bookingMode === "recurring") {
      if (!startDate || !endDate) {
        toast.error("Set both a start and end date.");
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        toast.error("Start date must be before end date.");
        return;
      }
      if (!isDateWithinAdminWindow(startDate)) {
        toast.error(`Admin recurring booking start dates are limited to the next ${ADMIN_MAX_DATE_RANGE_DAYS} days.`);
        return;
      }
      if (new Date(`${endDate}T00:00:00`) > getAdminMaxRangeEndDate(new Date(`${startDate}T00:00:00`))) {
        toast.error(`Recurring bookings can end at most ${ADMIN_MAX_DATE_RANGE_DAYS} days after the start date.`);
        return;
      }
      if (weekdays.length === 0) {
        toast.error("Select at least one day of the week.");
        return;
      }
      datesToBook = generateRecurringDates(startDate, endDate, weekdays);
      bookingType = "recurring";
    }

    if (datesToBook.length === 0) {
      toast.error("No dates to book. Check your date selection.");
      return;
    }

    if (datesToBook.length > ADMIN_MAX_DATE_RANGE_DAYS) {
      toast.error(`Admin bookings can span at most ${ADMIN_MAX_DATE_RANGE_DAYS} days.`);
      return;
    }

    const executeBooking = async () => {
      try {
        await dispatch.adminBookings.createMultipleBookings({
          ...bookingForm,
          dates: datesToBook,
          bookingType,
        });
        setBookingForm((prev) => ({
          ...createBookingForm(prev.seatId),
        }));
        const successMsg =
          bookingMode === "permanent"
            ? "Seat permanently assigned."
            : datesToBook.length > 1
            ? `${datesToBook.length} bookings created.`
            : "Booking created.";
        showBookingSuccessToast(successMsg);
        await fetchGlobalBookings();
        if (isSectionModalOpen && selectedSectionId) {
          await dispatch.adminBookings.loadSectionBookings({ sectionId: selectedSectionId });
        }
        if (bookingForm.seatId) {
          await dispatch.adminBookings.loadSeatDetails(bookingForm.seatId);
        }
        return true;
      } catch (error) {
        showBookingErrorToast(error.message || "Unable to create booking.");
      }
    };

    if (datesToBook.length > 366) {
      setConfirmDialog({
        isOpen: true,
        title: "Large Booking Creation",
        message: `This will create ${datesToBook.length} bookings. Continue?`,
        onConfirm: () => {
          setConfirmDialog({ isOpen: false });
          executeBooking();
        },
        onCancel: () => setConfirmDialog({ isOpen: false }),
      });
      return;
    }

    await executeBooking();
  };

  const handleCancelBooking = (bookingId, typeOrMeta = "", forceStandard = false, isSeatBlock = false) => {
    const bookingMeta = typeof typeOrMeta === "object" && typeOrMeta !== null ? typeOrMeta : null;
    const type = bookingMeta?.bookingType || typeOrMeta || "";
    const normalizedType = (type || "").toLowerCase();
    const isBlockedSeat =
      Boolean(bookingMeta?.isSeatBlock) ||
      isSeatBlock ||
      (!forceStandard && normalizedType.includes("block"));
    const useIndividualBlockCancel = Boolean(bookingMeta?.useIndividualBlockCancel);
    const useStandardFlow = Boolean(bookingMeta?.forceStandard) || forceStandard;
    const isPermanentBlock = isBlockedSeat && normalizedType.includes("permanent");
    const isRecurringBlock = isBlockedSeat && normalizedType.includes("recurring");
    const isPermanent = !useStandardFlow && (normalizedType === "permanent" || (typeof bookingId === "string" && bookingId.startsWith("perm-")));
    const isRecurring = !useStandardFlow && normalizedType === "recurring";
    
    setPromptDialog({
      isOpen: true,
      title: "Cancel Booking",
      message: "Cancellation reason:",
      defaultValue: "Cancelled by admin.",
      isDestructive: true,
      onConfirm: async (reason) => {
        setPromptDialog({ isOpen: false });
        try {
          if (useIndividualBlockCancel && isBlockedSeat) {
            await dispatch.adminBookings.cancelSeatBlock({ blockId: bookingId, reason });
            showBookingSuccessToast("Seat block cancelled.");
          } else if (isPermanentBlock) {
            await dispatch.adminBookings.cancelPermanentSeatBlock({ blockId: bookingId, reason });
            showBookingSuccessToast("Permanent seat block removed.");
          } else if (isRecurringBlock) {
            await dispatch.adminBookings.cancelRecurringSeatBlock({ blockId: bookingId, reason });
            showBookingSuccessToast("Recurring seat block cancelled.");
          } else if (isBlockedSeat) {
            await dispatch.adminBookings.cancelSeatBlock({ blockId: bookingId, reason });
            showBookingSuccessToast("Seat block cancelled.");
          } else if (isPermanent) {
            const permanentId = typeof bookingId === "string" && bookingId.startsWith("perm-") 
              ? bookingId.replace("perm-", "") 
              : bookingId;
            await dispatch.adminBookings.cancelPermanentBooking(permanentId);
            showBookingSuccessToast("Permanent assignment removed.");
          } else if (isRecurring) {
            await dispatch.adminBookings.cancelRecurringBooking(bookingId);
            showBookingSuccessToast("Recurring booking cancelled.");
          } else {
            await dispatch.adminBookings.cancelBooking({ bookingId, reason });
            showBookingSuccessToast("Booking cancelled.");
          }
          fetchGlobalBookings();
          if (isSectionModalOpen && selectedSectionId) {
            dispatch.adminBookings.loadSectionBookings({ sectionId: selectedSectionId });
          }
          // If we're looking at a specific seat, reload its details too
          if (selectedSeatDetails?.id || selectedSeatDetails?._id) {
            dispatch.adminBookings.loadSeatDetails(selectedSeatDetails.id || selectedSeatDetails._id);
          }
        } catch (error) {
          showBookingErrorToast(error.message || "Unable to cancel booking.");
        }
      },
      onCancel: () => setPromptDialog({ isOpen: false }),
    });
  };

  return (
    <div className="container mx-auto pb-10">
      {/* Header */}
      <BookingHeader />

      {/* All Bookings by Date */}
      <GlobalBookingsList
        allBookingsByDate={allBookingsByDate}
        filterDate={filterDate}
        setFilterDate={handleFilterDateChange}
        filterType={filterType}
        setFilterType={(type) => {
          setBookingsPage(1);
          setFilterType(type);
        }}
        filterSeatCapability={filterSeatCapability}
        setFilterSeatCapability={(seatCapability) => {
          setBookingsPage(1);
          setFilterSeatCapability(seatCapability);
        }}
        searchTerm={searchTerm}
        setSearchTerm={(value) => {
          setBookingsPage(1);
          setSearchTerm(value);
        }}
        currentPage={bookingsPage}
        setCurrentPage={setBookingsPage}
        pagination={allAdminBookingsPagination}
        handleCancelBooking={handleCancelBooking}
      />

      {/* Floor Plan */}
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <OfficeGrid
            office={office}
            sections={sections}
            loading={loading}
            selectedSectionId={selectedSectionId}
            dragState={dragState}
            previewPosition={previewPosition}
            handleSectionClick={handleSectionClick}
            handleSectionMouseDown={handleSectionMouseDown}
          >
            <AddSectionSidebar
              sectionForm={sectionForm}
              handleSectionFormChange={handleSectionFormChange}
              handleCreateSection={handleCreateSection}
              sectionsCount={sections.length}
            />
          </OfficeGrid>
        </div>

        <div className="w-full lg:w-[320px] shrink-0">
          <SectionSettingsSidebar
            selectedSection={selectedSection}
            sectionSize={sectionSize}
            setSectionSize={setSectionSize}
            seatDimensions={seatDimensions}
            setSeatDimensions={setSeatDimensions}
            selectedSectionSeatLimit={selectedSectionSeatLimit}
            handleDeleteSection={handleDeleteSection}
            setIsSectionModalOpen={setIsSectionModalOpen}
            handleUpdateSection={handleUpdateSection}
          />
        </div>
      </div>

      {isSectionModalOpen && selectedSection && (
        <SectionManagementModal
          selectedSection={selectedSection}
          users={users}
          setIsSectionModalOpen={setIsSectionModalOpen}
          sectionBookings={sectionBookings}
          workspaceBookings={bookings}
          activeSeatSlot={activeSeatSlot}
          newSeatLabel={newSeatLabel}
          setNewSeatLabel={setNewSeatLabel}
          newSeatType={newSeatType}
          setNewSeatType={setNewSeatType}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          handleSeatNameSubmit={handleSeatNameSubmit}
          handleUpdateSeat={handleUpdateSeat}
          handleDeleteSeat={handleDeleteSeat}
          handleEmptySlotClick={handleEmptySlotClick}
          handleCreateBooking={handleCreateBooking}
          handleRemoveDate={handleRemoveDate}
          handleBookingDateChange={handleBookingDateChange}
          handleToggleWeekday={handleToggleWeekday}
          handleCancelBooking={handleCancelBooking}
          previewDateCount={previewDateCount}
          seatLabelInputRef={seatLabelInputRef}
          selectedSeatDetails={selectedSeatDetails}
          loadSeatDetails={dispatch.adminBookings.loadSeatDetails}
          filterDate={filterDate}
          loadSectionBookings={dispatch.adminBookings.loadSectionBookings}
          mode={isSectionModalOpen}
        />
      )}

      <ConfirmDialog {...confirmDialog} />
      <PromptDialog {...promptDialog} />
    </div>
  );
};

export default Booking;
